import debounce from 'just-debounce';
import EventHandler from '@pixelunion/events'
import rimg from 'rimg-shopify';
import layout from '../Layout';
import MediaManager from './MediaManager';
import ProductCards from './ProductCards';
import ProductSlideshow from './ProductSlideshow';
import ProductMosaic from './ProductMosaic';
import ImageZoom from './ImageZoom';

export default class ProductGallery {
  constructor(options) {
    const defaults = {
      el: null,
      layout: 'slideshow',
      selectedImage: null,
    };

    this.config = {...defaults, ...options};

    this.productCards = this.config.el.querySelector('[data-product-cards]');
    this.productGallery = this.config.el.querySelector('[data-product-viewport-parent]');
    this.originalCards = this.productCards ? this.productCards.cloneNode(true) : null;
    this.originalGallery = this.productGallery.cloneNode(true);

    this.events = new EventHandler();

    // Reset loading state so images load properly when switching layouts in the TE.
    // Otherwise the first few images may have started loading by this point, so when we
    // re-insert them into the DOM they have a `data-rimg="loading"` attribute and aren't
    // detected by `rimg.watch()`.
    const resetRimgLoad = el => el.querySelectorAll('[data-rimg="loading"]').forEach(img => img.dataset.rimg = 'lazy');

    if (this.productCards) {
      resetRimgLoad(this.productCards);
    }
    resetRimgLoad(this.originalGallery);

    this.initialized = false;

    this.onResize = debounce(this._onResize, 15).bind(this);
    this._onResize();
    this.initialized = true;

    layout.onBreakpointChange(this.onResize);

    const pswpJsonElement = JSON.parse(this.config.el.querySelector('[data-photoswipe-images]').innerHTML);

    if (this.config.el.classList.contains('product-gallery-zoom')) {
      this.imageZoom = new ImageZoom(pswpJsonElement.images);
      this.events.register(this.config.el, 'click', e => {
        if (!('imageZoom' in e.target.dataset)) return;
        this.imageZoom.open(e.target);
      })
    }
  }

  switchImage(id) {
    if (!id) {
      return;
    }

    if (this.gallery && typeof this.gallery.switchImage === 'function') {
      this.gallery.switchImage(id);
    }
  }

  unload() {
    layout.offBreakpointChange(this.onResize);

    this.events.unregisterAll();

    if (this.mediaManager) {
      this.mediaManager.unload();
    }

    if (this.gallery && typeof this.gallery.destroy === 'function') {
      this.gallery.destroy();
    }

    if (this.imageZoom) {
      this.imageZoom.unload();
    }
  }

  _onResize() {
    if (document.fullscreenElement) return;

    switch (layout.getBreakpoint()) {
      case 'XS':
      case 'S':
        this._initMobile();
        this.switchImage(this.config.selectedImage);
        break;
      default:
        this._initDesktop();
        this.switchImage(this.config.selectedImage);
        break;
    }
  }

  _initMobile() {
    this._destroyGallery();

    this.galleryEl = this.config.el.querySelector('[data-product-cards]');

    this.mediaManager = new MediaManager(this.galleryEl, {
      enable_video_autoplay: this.config.settings.gallery_video_autoplay,
      enable_video_looping: this.config.settings.gallery_video_looping,
    });

    this.gallery = new ProductCards({
      el: this.galleryEl,
      pagination: this.config.el.querySelector('[data-product-card-pagination]'),
      mediaManager: this.mediaManager,
      viewInYourSpace: this.config.el.querySelector('[data-shopify-xr].product-cards--viewinyourspace'),
    });
  }

  _initDesktop() {
    this._destroyGallery();

    switch (this.config.layout) {
      case 'slideshow':
        this.galleryEl = this.config.el.querySelector('[data-product-slideshow]');

        this.mediaManager = new MediaManager(this.galleryEl, {
          enable_video_autoplay: this.config.settings.gallery_video_autoplay,
          enable_video_looping: this.config.settings.gallery_video_looping,
        });

        this.gallery = new ProductSlideshow({
          el: this.galleryEl,
          mediaManager: this.mediaManager,
        });
        break;
      case 'mosaic':
        this.galleryEl = this.config.el.querySelector('[data-product-mosaic]');

        this.mediaManager = new MediaManager(this.galleryEl, {
          enable_video_autoplay: this.config.settings.gallery_video_autoplay,
          enable_video_looping: this.config.settings.gallery_video_looping,
        });

        this.gallery = new ProductMosaic({
          el: this.galleryEl,
        });
        break;
      case 'one-column':
      case 'two-column':
        this.galleryEl = this.config.el.querySelector('[data-product-media-list]');

        this.mediaManager = new MediaManager(this.galleryEl, {
          enable_video_autoplay: this.config.settings.gallery_video_autoplay,
          enable_video_looping: this.config.settings.gallery_video_looping,
        });
        break;
      default:
        break;
    }
  }

  _destroyGallery() {
    if (!this.initialized) return;

    if (this.mediaManager) {
      this.mediaManager.unload();
    }

    if (this.gallery && typeof this.gallery.destroy === 'function') {
      this.gallery.destroy();
    }

    const replaceNode = (oldNode, newNode) => oldNode.parentNode.replaceChild(newNode, oldNode);

    if (this.originalCards) {
      // Quickshops don't have cards
      const clonedCards = this.originalCards.cloneNode(true);
      replaceNode(this.productCards, clonedCards);
      this.productCards = clonedCards;
      rimg.watch(this.productCards);
    }

    const clonedGallery = this.originalGallery.cloneNode(true);
    replaceNode(this.productGallery, clonedGallery);
    this.productGallery = clonedGallery;
    rimg.watch(this.productGallery);
  }
}
